import {
	CheckCircle,
	CircleCheckBig,
	FolderMinus,
	FolderPlus,
	FolderSymlink,
	Mail,
	MailCheck,
	MailOpen,
	MailWarning,
	Phone,
	Pin,
	PinOff,
	Tag,
	User,
} from 'lucide-react';
import { ActionSheet } from '@/components/mobile';
import { getFolderTitle, useConversationQuickActions } from './useConversationQuickActions';

const EMPTY_CONVERSATION = {};

/**
 * The phone version of the inbox row's right-click menu: opened by a long
 * press or the "More" swipe action. Assign, Tags and Move to folder drill in.
 */
export function ConversationActionSheet({
	conversation,
	title,
	open,
	onOpenChange,
	onCreateFolder,
	onShowVoiceUpsell,
	onSelectConversation,
}) {
	const actions = useConversationQuickActions(conversation || EMPTY_CONVERSATION, {
		onShowVoiceUpsell,
	});

	if (!conversation) {
		return <ActionSheet open={false} onOpenChange={onOpenChange} title={title || 'Conversation'} />;
	}

	const {
		_business,
		customer,
		folders,
		isRead,
		customerOnActiveCall,
		isCallDisabled,
		hasChannelChoice,
		preferredChannel,
		currentTags,
		availableTags,
		isPinnedForMe,
		isPinnedForBusiness,
		activeUsers,
		businessDepartments,
		selectedAssignees,
	} = actions;

	const switchTo = preferredChannel.toLowerCase() === 'phone' ? 'email' : 'phone';
	const currentFolder = conversation._folder
		? folders.find((folder) => folder._id === conversation._folder)
		: null;
	const assigneeSummary = (() => {
		if (!selectedAssignees.length) return 'Unassigned';
		if (selectedAssignees.length > 1) return `${selectedAssignees.length} people`;
		const [value] = selectedAssignees;
		if (value.startsWith('DEPARTMENT_')) {
			return businessDepartments.find((d) => `DEPARTMENT_${d._id}` === value)?.name || '';
		}
		const user = activeUsers.find((u) => `USER_${u._id}` === value);
		return user?.name || user?.username || '';
	})();

	const sections = [
		customer?.phone && {
			id: 'call',
			items: [
				{
					id: 'call',
					label: customerOnActiveCall ? 'Customer is already on a call' : 'Call customer',
					icon: Phone,
					disabled: isCallDisabled,
					onSelect: actions.handleCallCustomer,
				},
			],
		},
		{
			id: 'state',
			items: [
				{
					id: 'read',
					label: isRead ? 'Mark as unread' : 'Mark as read',
					icon: isRead ? MailWarning : MailCheck,
					onSelect: actions.handleToggleRead,
				},
				{
					id: 'open',
					label: conversation.open ? 'Close conversation' : 'Reopen conversation',
					icon: conversation.open ? CheckCircle : MailOpen,
					onSelect: actions.handleToggleOpen,
				},
				{
					id: 'pin-me',
					label: isPinnedForMe ? 'Unpin for me' : 'Pin for me',
					icon: isPinnedForMe ? PinOff : Pin,
					onSelect: () => actions.handleTogglePin({ scope: 'user', pinned: !isPinnedForMe }),
				},
				{
					id: 'pin-everyone',
					label: isPinnedForBusiness ? 'Unpin for everyone' : 'Pin for everyone',
					icon: isPinnedForBusiness ? PinOff : Pin,
					onSelect: () =>
						actions.handleTogglePin({ scope: 'business', pinned: !isPinnedForBusiness }),
				},
			],
		},
		{
			id: 'organize',
			items: [
				{
					id: 'assign',
					label: 'Assign',
					icon: User,
					page: 'assign',
					disabled: !_business,
					trailing: assigneeSummary,
				},
				{
					id: 'tags',
					label: 'Tags',
					icon: Tag,
					page: 'tags',
					disabled: !customer,
					trailing: currentTags.length ? String(currentTags.length) : undefined,
				},
				{
					id: 'folder',
					label: 'Move to folder',
					icon: FolderSymlink,
					page: 'folders',
					trailing: currentFolder ? getFolderTitle(currentFolder) : undefined,
				},
			],
		},
		{
			id: 'more',
			items: [
				hasChannelChoice && {
					id: 'channel',
					label: `Switch to ${switchTo}`,
					icon: switchTo === 'email' ? Mail : Phone,
					onSelect: () => actions.handleSelectPreferredChannel(switchTo),
				},
				onSelectConversation && {
					id: 'select',
					label: 'Select',
					icon: CircleCheckBig,
					onSelect: onSelectConversation,
				},
			].filter(Boolean),
		},
	].filter((section) => section && section.items.length);

	const pages = [
		{
			id: 'assign',
			title: 'Assign',
			searchable: activeUsers.length + businessDepartments.length > 8,
			searchPlaceholder: 'Search people',
			sections: [
				{
					id: 'none',
					items: [
						{
							id: 'unassigned',
							label: 'Unassigned',
							checked: selectedAssignees.length === 0,
							keepOpen: true,
							onSelect: () => actions.handleSelectAssignees([]),
						},
					],
				},
				activeUsers.length > 0 && {
					id: 'users',
					title: 'Users',
					items: activeUsers.map((user) => ({
						id: user._id,
						label: user.name || user.username,
						checked: selectedAssignees.includes(`USER_${user._id}`),
						keepOpen: true,
						onSelect: () => actions.handleToggleUserAssignee(user._id),
					})),
				},
				businessDepartments.length > 0 && {
					id: 'departments',
					title: 'Departments',
					items: businessDepartments.map((department) => ({
						id: department._id,
						label: department.name,
						checked: selectedAssignees.includes(`DEPARTMENT_${department._id}`),
						keepOpen: true,
						onSelect: () => actions.handleToggleDepartmentAssignee(department._id),
					})),
				},
			].filter(Boolean),
		},
		{
			id: 'tags',
			title: 'Tags',
			searchable: availableTags.length > 8,
			searchPlaceholder: 'Search tags',
			emptyText: 'No matching tags',
			sections: availableTags.length
				? [
						{
							id: 'tags',
							items: availableTags.map((tag) => ({
								id: tag,
								label: tag,
								checked: currentTags.includes(tag),
								keepOpen: true,
								onSelect: () => actions.handleToggleCustomerTag(tag),
							})),
						},
					]
				: [{ id: 'none', items: [{ id: 'none', label: 'No tags available', disabled: true }] }],
		},
		{
			id: 'folders',
			title: 'Move to folder',
			searchable: folders.length > 8,
			searchPlaceholder: 'Search folders',
			sections: [
				folders.length > 0 && {
					id: 'folders',
					items: folders.map((folder) => ({
						id: folder._id,
						label: getFolderTitle(folder),
						checked: conversation._folder === folder._id,
						onSelect: () => actions.handleFolderAction(folder._id),
					})),
				},
				{
					id: 'manage',
					items: [
						{ id: 'create', label: 'Create folder', icon: FolderPlus, onSelect: onCreateFolder },
						conversation._folder && {
							id: 'remove',
							label: 'Remove from folder',
							icon: FolderMinus,
							destructive: true,
							onSelect: () => actions.handleRemoveFolder(conversation._folder),
						},
					].filter(Boolean),
				},
			].filter(Boolean),
		},
	];

	return (
		<ActionSheet
			open={open}
			onOpenChange={onOpenChange}
			title={title || 'Conversation'}
			sections={sections}
			pages={pages}
		/>
	);
}
